import MainURL from '../../Admin/MainURL';

export type ProjectCategory = 'own' | 'outsource';

export interface ProjectScreen {
  imageUrl: string;
  caption: string;
}

export interface ApiProject {
  id: number;
  title: string;
  description: string;
  category: string;
  tech_stacks: string | string[];
  image_url: string;
  project_url?: string;
  web_url?: string;
  android_url?: string;
  ios_url?: string;
  headline?: string;
  client_name?: string;
  industry?: string;
  period?: string;
  scope?: string;
  platform?: string;
  challenge?: string;
  approach?: string;
  result?: string;
  screens?: { image_url: string; caption?: string }[];
  next?: { id: number; title: string } | null;
}

export interface Project {
  id: number;
  title: string;
  description: string;
  category: ProjectCategory;
  thumbnailUrl: string;
  techStack: string[];
  projectUrl: string;
  webUrl: string;
  androidUrl: string;
  iosUrl: string;
  headline: string;
  client: string;
  industry: string;
  period: string;
  scope: string;
  platform: string;
  challenge: string;
  approach: string;
  result: string;
  screens: ProjectScreen[];
}

export const PROJECTS_API_URL = `${MainURL}/api/projects`;

export const CATEGORY_LABELS: Record<ProjectCategory, string> = {
  own: '자체 플랫폼',
  outsource: '외주 프로젝트',
};

const parseTechStacks = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => item.trim()).filter(Boolean);
  }

  return (value || '').split(',').map((item) => item.trim()).filter(Boolean);
};

// 'own'이 아니면(기존 'web'/'app' 값 포함) 외주 프로젝트로 분류
const normalizeCategory = (value: string): ProjectCategory =>
  value === 'own' ? 'own' : 'outsource';

export const normalizeProject = (raw: ApiProject): Project => ({
  id: raw.id,
  title: raw.title,
  description: raw.description,
  category: normalizeCategory(raw.category),
  thumbnailUrl: raw.image_url || '',
  techStack: parseTechStacks(raw.tech_stacks),
  projectUrl: raw.project_url || '',
  // 웹 링크가 비어 있으면 기존 project_url 을 웹 링크로 사용
  webUrl: raw.web_url || raw.project_url || '',
  androidUrl: raw.android_url || '',
  iosUrl: raw.ios_url || '',
  headline: raw.headline || '',
  client: raw.client_name || '',
  industry: raw.industry || '',
  period: raw.period || '',
  scope: raw.scope || '',
  platform: raw.platform || '',
  challenge: raw.challenge || '',
  approach: raw.approach || '',
  result: raw.result || '',
  screens: (raw.screens || [])
    .filter((screen) => screen?.image_url)
    .map((screen) => ({ imageUrl: screen.image_url, caption: screen.caption || '' })),
});

const EMPTY_DETAIL = {
  thumbnailUrl: '',
  challenge: '',
  period: '',
  result: '',
  screens: [],
};

export const FEATURED_PROJECTS: Project[] = [
  {
    ...EMPTY_DETAIL,
    id: -1000,
    title: '사역자모아',
    description:
      '교회와 사역자를 연결하는 구인구직·사역 지원 플랫폼. 채용공고, 수련회 장소·강사, AI 설교지원·콘티·찬양 찾기, 커뮤니티까지 한곳에서 제공합니다.',
    category: 'own',
    techStack: ['React', 'Node.js', 'MySQL', 'AWS'],
    projectUrl: 'https://ministermore.co.kr/',
    webUrl: 'https://ministermore.co.kr/',
    androidUrl: '',
    iosUrl: '',
    headline: '교회와 사역자를 잇는 사역 지원 플랫폼',
    client: '예플랫 (자체 서비스)',
    industry: '교회 · 사역 지원',
    scope: '기획 · UX/UI · 웹 개발 · 운영',
    platform: 'Web',
    approach:
      '채용공고, 수련회 장소·강사, AI 설교지원·콘티·찬양 찾기, 커뮤니티를 한 플랫폼에 모았습니다.',
  }
];

export interface PlatformLink {
  key: 'web' | 'android' | 'ios';
  label: string;
  url: string;
}

// 입력된 링크만 웹 → 안드로이드 → 애플 순서로 반환
export const getPlatformLinks = (project: Project): PlatformLink[] =>
  [
    { key: 'web' as const, label: '웹', url: project.webUrl },
    { key: 'android' as const, label: '안드로이드 앱', url: project.androidUrl },
    { key: 'ios' as const, label: '애플 앱', url: project.iosUrl },
  ].filter((link) => link.url);

export const mergeFeaturedProjects = (projects: Project[]): Project[] => {
  const existingTitles = new Set(projects.map((project) => project.title.trim().toLowerCase()));
  const missing = FEATURED_PROJECTS.filter(
    (project) => !existingTitles.has(project.title.trim().toLowerCase())
  );
  return [...missing, ...projects];
};
